import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CreditCard, Landmark, Zap } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { money } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const presets = [5000, 10000, 20000, 50000];
type Method = "card" | "instant" | "direct_debit";

const METHODS: { id: Method; label: string; hint: string; icon: React.ElementType }[] = [
  { id: "card", label: "Card", hint: "Credit lands instantly", icon: CreditCard },
  { id: "instant", label: "From Bank Account", hint: "Approve in your banking app · minutes", icon: Zap },
  { id: "direct_debit", label: "Direct Debit", hint: "From your mandate · 3–5 working days", icon: Landmark },
];

export function TopUpPanel({ mandateActive, loaded, cardEnabled, stripeTest }: { mandateActive: boolean; loaded: boolean; cardEnabled: boolean; stripeTest: boolean }) {
  const [params] = useSearchParams();
  const wanted = Math.round(Number(params.get("amount") || 0) * 100);
  const [amount, setAmount] = useState(wanted > 0 ? wanted : 10000);
  const [method, setMethod] = useState<Method>(cardEnabled ? "card" : "direct_debit");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  const submit = async () => {
    setBusy(true);
    try {
      if (method === "card") {
        const { data } = await api.post("/stripe/topup", { amount_pence: amount, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      if (method === "instant") {
        const { data } = await api.post("/instant/start", { amount_pence: amount });
        window.location.href = data.authorisation_url;
        return;
      }
      const { data } = await api.post("/wallet/topup", { amount_pence: amount });
      toast.success(data.message);
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["payments"] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const ddBlocked = method === "direct_debit" && (!loaded || !mandateActive);
  const cta = method === "card" ? `Pay ${money(amount)} by card` : method === "instant" ? `Pay ${money(amount)} from my bank` : ddBlocked ? "Connect your bank first" : `Collect ${money(amount)} by Direct Debit`;

  return (
    <div data-testid="topup-panel">
      <div className="eyebrow">One-off · Add credit</div>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl">Add credit to your account</h2>
      <p className="mt-3 text-sm text-neutral-600">Credit is added to your account only once the payment is confirmed — card and bank account payments confirm within moments.</p>

      <div className="mt-8 grid gap-px sm:grid-cols-3" role="radiogroup" data-testid="topup-method-group">
        {METHODS.filter((m) => m.id !== "card" || cardEnabled).map((m) => {
          const Icon = m.icon;
          const active = method === m.id;
          return (
            <button key={m.id} role="radio" aria-checked={active} onClick={() => setMethod(m.id)}
              className={`border p-4 text-left transition-colors duration-200 ${active ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border hover:border-[var(--ink)]"}`}
              data-testid={`topup-method-${m.id}`}>
              <Icon className="h-4 w-4" strokeWidth={1.25} />
              <div className="mt-3 text-sm font-medium">{m.label}</div>
              <div className={`mt-1 text-xs ${active ? "text-[var(--paper)]/70" : "text-neutral-500"}`}>{m.hint}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-4 gap-2">
        {presets.map((p) => (
          <button key={p} onClick={() => setAmount(p)} className={`border py-3 text-sm tabular-nums transition-colors duration-200 ${amount === p ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border hover:border-[var(--ink)]"}`} data-testid={`topup-preset-${p}`}>{money(p)}</button>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <span className="font-serif text-2xl">£</span>
        <Input type="number" min={10} max={5000} step={5} value={amount / 100} onChange={(e) => setAmount(Math.round(Number(e.target.value) * 100))} className="rounded-none bg-transparent text-lg tabular-nums" data-testid="topup-amount-input" />
      </div>
      <Button onClick={submit} disabled={ddBlocked || busy || amount < 1000} className="mt-6 w-full rounded-none py-6 text-base hover-lift" data-testid="topup-submit-button">
        {busy ? "One moment…" : cta}
      </Button>
      <p className="mt-3 text-xs text-neutral-500">
        Minimum £10, maximum £5,000 per top-up.
        {method === "card" && stripeTest && <span className="ml-1 text-[var(--wine)]" data-testid="stripe-test-notice">Card payments are in test mode — use card 4242 4242 4242 4242.</span>}
        {method === "direct_debit" && !mandateActive && loaded && <span className="ml-1">Set up your Direct Debit mandate above to use this method.</span>}
      </p>
    </div>
  );
}
